import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { pipelineService, CANAIS } from '@/features/pipeline/services/pipeline.service';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/_authenticated/pipeline/leads/novo')({
  component: NovoLead,
});

function NovoLead() {
  return (
    <PermissionGate permission={Permission.PIPELINE_MANAGE_LEADS}>
      <NovoLeadContent />
    </PermissionGate>
  );
}

function NovoLeadContent() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    nome: '',
    empresa: '',
    email: '',
    telefone: '',
    canalOrigem: 'whatsapp',
    mensagemAbordagem: '',
    observacoes: '',
  });

  const m = useMutation({
    mutationFn: () =>
      pipelineService.createLead({
        nome: form.nome.trim(),
        empresa: form.empresa.trim() || undefined,
        email: form.email.trim() || undefined,
        telefone: form.telefone.trim() || undefined,
        canalOrigem: form.canalOrigem as any,
        mensagemAbordagem: form.mensagemAbordagem.trim() || undefined,
        observacoes: form.observacoes.trim() || undefined,
        etapa: 'lista',
      }),
    onSuccess: (l) => {
      qc.invalidateQueries({ queryKey: ['pipeline-leads'] });
      toast.success('Lead criado');
      navigate({ to: '/pipeline/leads/$id', params: { id: l.id } });
    },
    onError: (e: any) => toast.error('Erro', { description: e?.message }),
  });

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-4">
      <Link
        to="/pipeline/leads"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Leads
      </Link>
      <Card>
        <CardHeader>
          <CardTitle>Novo lead</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Nome *">
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
            </Field>
            <Field label="Empresa">
              <Input value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })} />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Telefone">
              <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
            </Field>
          </div>
          <Field label="Canal de origem *">
            <Select value={form.canalOrigem} onValueChange={(v) => setForm({ ...form, canalOrigem: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CANAIS.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Mensagem de abordagem">
            <Textarea
              rows={4}
              value={form.mensagemAbordagem}
              onChange={(e) => setForm({ ...form, mensagemAbordagem: e.target.value })}
              placeholder="A primeira mensagem usada para contatar este lead..."
            />
          </Field>
          <Field label="Observações">
            <Textarea rows={3} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => navigate({ to: '/pipeline/leads' })}>
              Cancelar
            </Button>
            <Button disabled={!form.nome.trim() || m.isPending} onClick={() => m.mutate()}>
              {m.isPending ? 'Salvando...' : 'Criar lead'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
